import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || 'jiaoyou');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryArticles = articles.filter((a) => a.categoryId === activeSlug);

  const handleCategoryChange = useCallback(
    (newSlug: string) => {
      setActiveSlug(newSlug);
      navigate(`/category/${newSlug}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [navigate],
  );

  return (
    <>
      <Navbar />

      <main className="pt-16">
        {/* Header */}
        <section className="py-12 md:py-16 px-6 md:px-10 bg-background-100 border-b border-background-200/70">
          <div className="mx-auto" style={{ maxWidth: '1200px' }}>
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">{activeCategory.name}</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-semibold text-foreground-950 font-[var(--font-heading)]">
              {activeCategory.name}
            </h1>
            <p className="mt-2 text-sm text-foreground-600">{activeCategory.description}</p>
            <p className="mt-1 text-xs text-foreground-500">共 {categoryArticles.length} 篇文章</p>
          </div>
        </section>

        <section className="py-10 md:py-14 px-6 md:px-10 bg-background-50">
          <div className="mx-auto flex flex-col lg:flex-row gap-8" style={{ maxWidth: '1200px' }}>
            {/* Sidebar */}
            <aside className="w-full lg:w-56 flex-shrink-0">
              <div className="lg:sticky lg:top-24 bg-background-100 rounded-xl border border-background-200/70 p-3">
                <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3 px-2">
                  内容板块
                </h3>
                <nav className="flex flex-col gap-1">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryChange(cat.slug)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all text-left cursor-pointer ${
                        activeSlug === cat.slug
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-200/70'
                      }`}
                    >
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          activeSlug === cat.slug
                            ? 'bg-primary-500 text-white'
                            : 'bg-background-200/70 text-foreground-500'
                        }`}
                      >
                        <i className={`${cat.icon} text-sm`}></i>
                      </div>
                      <span>{cat.name}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Articles */}
            <div className="flex-1 min-w-0">
              {categoryArticles.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类暂无文章</p>
                </div>
              ) : (
                <div className="flex flex-col gap-5">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group flex flex-col sm:flex-row gap-5 p-5 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                    >
                      <div className="w-full sm:w-56 h-40 sm:h-36 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={article.imageUrl}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="flex flex-col justify-between min-w-0 flex-1">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <span className="text-xs text-primary-500 font-medium whitespace-nowrap">
                              {article.categoryName}
                            </span>
                            <span className="text-xs text-foreground-400">&middot;</span>
                            <span className="text-xs text-foreground-500 whitespace-nowrap">
                              {article.readTime} 分钟阅读
                            </span>
                          </div>
                          <h2 className="text-lg font-semibold text-foreground-950 leading-snug font-[var(--font-heading)]">
                            {article.title}
                          </h2>
                          <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                            {article.excerpt}
                          </p>
                        </div>
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                          {article.tags.map((tag) => (
                            <span
                              key={tag}
                              className="text-xs px-2 py-0.5 rounded-md bg-background-200/70 text-foreground-600 whitespace-nowrap"
                            >
                              {tag}
                            </span>
                          ))}
                          <span className="ml-auto text-xs text-foreground-500 whitespace-nowrap">
                            {article.publishDate}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Schema.org */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: `${activeCategory.name} - 男同网`,
              description: activeCategory.description,
              url: `https://nantongwang.com/category/${activeCategory.slug}`,
              isPartOf: {
                '@type': 'WebSite',
                name: '男同网',
                url: 'https://nantongwang.com',
              },
              breadcrumb: {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: '首页', item: 'https://nantongwang.com/' },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: activeCategory.name,
                  },
                ],
              },
            }),
          }}
        />
      </main>

      <Footer />
    </>
  );
}